import { useContext, useEffect, useRef, useState } from 'react';
import { NavLink as RouterLink, useNavigate } from 'react-router-dom';
import {
  Button,
  CircularProgress,
  Divider,
  ListItem,
  Popover
} from '@mui/material';
import { Cookies } from 'react-cookie';
import { parentLogout, switchToStudent } from 'src/services/auth.service';
import { useSnackbar } from 'notistack';
import { routeURL } from 'src/utils/properties';
import { getStudentsByParentId } from 'src/services/Parent/parent.service';
import { Student } from 'src/models/student.interface';
import { getStudentById } from 'src/services/Student/student.service';
import { errorHandler } from 'src/utils/error-handler.utils';
import { useScreenBreakpoints } from 'src/utils/screenBreakpoints';
import {
  GridContainer,
  GridItem,
  Text1,
  Text2,
  Text3
} from 'src/components/StyledComponents/Index';
import {
  ButtonText,
  MyChildListGridItem,
  ParentPopoverGridContainer,
  UserBoxButton,
  UserBoxLabel,
  UserBoxText
} from 'src/components/StyledComponents/Navigation';
import CreateStudent from './CreateStudent';
import { MyAccount } from 'src/components/StyledComponents/StudentLogin';
import { LoggedInUserContext } from 'src/contexts/LoggedInUsersContext';
import {
  ProfileAvatar,
  PopeOverProfileAvatar,
  PopoverParentProfileMyKidsAvatar,
  HeaderLoaderBox
} from 'src/components/StyledComponents/Header';
import { clearUserData, setLocalStorageData } from 'src/utils/functions.utils';
import { LoginType } from 'src/enums';

function HeaderUserbox() {
  const { enqueueSnackbar } = useSnackbar();
  const { contextLoggedInUser, getContextLoggedInUser } =
    useContext(LoggedInUserContext);
  const navigate = useNavigate();
  const { isSmallScreen } = useScreenBreakpoints();

  const [students, setStudents] = useState<Student[]>([]);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const ref = useRef<any>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (contextLoggedInUser === null) {
        getContextLoggedInUser();
      } else {
        setLoading(false);
      }
    };

    const intervalId = setInterval(() => {
      fetchUserData();
    }, 1000);

    return () => clearInterval(intervalId);
  }, [contextLoggedInUser, getContextLoggedInUser]);

  const handleOpen = async (): Promise<void> => {
    try {
      const response: any = await getStudentsByParentId();
      setStudents(response.students || []);
      setTimeout(() => {
        setOpen(true);
      }, 100);
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleClose = (): void => {
    setOpen(false);
    setIsCreateOpen(false);
  };

  const switchToStudentAcc = async (student: Student): Promise<void> => {
    try {
      const input = {
        email: student.email,
        studentId: student.id
      };

      const response = await switchToStudent(input);
      await getStudentById().then(
        (response: any) => {
          setLocalStorageData({
            ...response.user,
            userType: LoginType.STUDENT,
            parentId: contextLoggedInUser?.id
          });
          getContextLoggedInUser();
        },
        (error: any) => errorHandler(error)
      );

      enqueueSnackbar(response.data.message, { variant: 'success' });

      const loggedInUser = JSON.parse(
        localStorage.getItem('loggedInUser') || '{}'
      );

      if (
        loggedInUser.userType === LoginType.STUDENT &&
        loggedInUser.parentId !== null
      ) {
        navigate(routeURL.STUDENT + routeURL.DASHBOARD);
      } else {
        enqueueSnackbar('Failed to switch to student account', {
          variant: 'error'
        });
      }
    } catch (error) {
      const errorMessage = errorHandler(error);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleLogOut = () => {
    parentLogout().then(
      async (response: any) => {
        clearUserData();
        enqueueSnackbar(response.message, { variant: 'success' });
        redirectToLogin();
      },
      async (error: any) => {
        clearUserData();
        enqueueSnackbar(errorHandler(error), { variant: 'success' });
        redirectToLogin();
      }
    );
  };

  const redirectToLogin = () => {
    const loginRoute =
      contextLoggedInUser?.userType === LoginType.PARENT
        ? routeURL.PARENT_LOGIN
        : routeURL.STUDENT_LOGIN;
    navigate(loginRoute);
  };

  return !loading ? (
    <>
      <UserBoxButton ref={ref} onClick={handleOpen}>
        <ProfileAvatar />
        {!isSmallScreen && (
          <UserBoxText>
            <UserBoxLabel variant="body1">
              {contextLoggedInUser?.firstName}
            </UserBoxLabel>
          </UserBoxText>
        )}
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <ParentPopoverGridContainer rowSpacing="10px">
          <GridItem md={12} xs={12}>
            <GridContainer justifyContent="center">
              <GridItem md={3} xs={3} margin="auto">
                <PopeOverProfileAvatar />
              </GridItem>
              <GridItem xs={6} margin="auto">
                <GridContainer rowSpacing="20px">
                  <GridItem md={12}>
                    <GridContainer>
                      <GridItem md={12}>
                        <Text1 fontWeight={700}>
                          {contextLoggedInUser?.firstName}
                        </Text1>
                      </GridItem>
                      <GridItem md={12}>
                        <Text3>{contextLoggedInUser?.email}</Text3>
                      </GridItem>
                    </GridContainer>
                  </GridItem>
                  <GridItem md={12}>
                    <MyAccount
                      disableRipple
                      component={RouterLink}
                      to="my-account"
                      onClick={handleClose}
                    >
                      My Account
                    </MyAccount>
                  </GridItem>
                </GridContainer>
              </GridItem>
            </GridContainer>
          </GridItem>
          {students.length > 0 && (
            <MyChildListGridItem md={12} xs={12}>
              <GridContainer rowSpacing="16px" p={1}>
                <GridItem md={12}>
                  <Text1 fontWeight={700}>My Children</Text1>
                </GridItem>
                {students.map((student) => (
                  <ListItem
                    key={student.id}
                    button
                    onClick={() => switchToStudentAcc(student)}
                  >
                    <GridItem md={12} xs={12}>
                      <GridContainer>
                        <GridItem md={2} xs={3} margin="auto">
                          <PopoverParentProfileMyKidsAvatar
                            src={student.profile}
                          />
                        </GridItem>
                        <GridItem md={6} xs={6} margin="auto">
                          <GridContainer>
                            <GridItem md={12}>
                              <Text2>{student.firstName}</Text2>
                            </GridItem>
                            <GridItem md={12}>
                              <Text3>{student.email}</Text3>
                            </GridItem>
                          </GridContainer>
                        </GridItem>
                      </GridContainer>
                    </GridItem>
                  </ListItem>
                ))}
              </GridContainer>
            </MyChildListGridItem>
          )}
          <GridItem md={12} xs={12}>
            <Divider />
          </GridItem>
          <GridItem md={12} xs={12} pt={2}>
            <GridContainer justifyContent="space-between">
              <Button variant="outlined" onClick={() => setIsCreateOpen(true)}>
                <ButtonText>Add Child</ButtonText>
              </Button>
              <Button variant="outlined" onClick={handleLogOut}>
                <ButtonText>Log out</ButtonText>
              </Button>
            </GridContainer>
          </GridItem>
        </ParentPopoverGridContainer>
      </Popover>
      {isCreateOpen && (
        <CreateStudent open={isCreateOpen} onClose={handleClose} />
      )}
    </>
  ) : (
    <HeaderLoaderBox>
      <CircularProgress size={20} thickness={3} />
    </HeaderLoaderBox>
  );
}

export default HeaderUserbox;
